import type { DynamicCore } from '../../../client/core/types/DynamicCore';
import { BaseError } from '../../../errors/base/BaseError';
import { extractSessionId } from '../../../modules/auth/extractSessionId/extractSessionId';
import { getOperatingSystem } from '../../../utils/getOperatingSystem/getOperatingSystem';
import { getPlatform } from '../../../utils/getPlatform/getPlatform';
import { getUserAgent } from '../../../utils/getUserAgent/getUserAgent';
import type { Logger } from '../../logger/logger.types';
import type {
  FunctionInstrumentationEvent,
  InstrumentationContext,
  InstrumentationPayloadSelectors,
  SafePayload,
} from '../instrumentation.types';
import { scrubParameters } from '../scrubParameters';

// Only the error's class name and (for BaseError) its stable code are logged.
// The message and stack are deliberately omitted: thrown messages routinely
// interpolate argument values, identifiers, and filesystem paths, and in V8
// the stack string embeds that same message as its first line — so forwarding
// the stack would re-introduce the exact leak this model exists to prevent.
const toErrorFields = (
  error: unknown
): Pick<FunctionInstrumentationEvent, 'errorCode' | 'errorName'> => {
  if (error instanceof BaseError) {
    return { errorCode: error.code, errorName: error.name };
  }

  if (error instanceof Error) {
    return { errorName: error.name };
  }

  return { errorName: 'UnknownError' };
};

// Stable envelope fields for a single invocation. None derive from the call's
// arguments or return value, so the context is always safe to forward.
// eslint-disable-next-line custom-rules/one-function-per-file
const buildContext = (
  core: DynamicCore
): Omit<InstrumentationContext, 'status' | 'timestamp'> => {
  const state = core.state.get();

  return {
    environmentId: core.environmentId,
    operatingSystem: getOperatingSystem(),
    platform: getPlatform(),
    registeredExtensionKeys: Array.from(core.extensions),
    sdkSessionId: core.sdkSessionId,
    sdkVersion: core.version,
    tokenSessionId: state.token ? extractSessionId(state.token) : null,
    userAgent: getUserAgent(),
    userId: state.user?.id ?? null,
  };
};

/**
 * Runs an explicit payload selector and scrubs its output. Returns `undefined`
 * when there is no selector. Selector (and scrub) errors are swallowed so a
 * faulty selector can never break the wrapped function or fabricate a
 * 'rejected' event — instrumentation stays purely observational. The swallowed
 * error is surfaced on the `debug` logger so internal devs can still spot a
 * broken selector without it ever reaching a transport.
 */
// eslint-disable-next-line custom-rules/one-function-per-file
const safeSelect = <T>({
  functionName,
  logger,
  piiFields,
  selector,
  value,
}: {
  functionName: string;
  logger: Logger;
  piiFields: string[];
  selector: ((value: T) => SafePayload) | undefined;
  value: T;
}): SafePayload | undefined => {
  if (!selector) {
    return undefined;
  }

  try {
    return scrubParameters({ piiFields, value: selector(value) }) as SafePayload;
  } catch (error) {
    logger.debug(
      `[instrumentFunction] payload selector for "${functionName}" threw; emitting event without payload`,
      error
    );

    return undefined;
  }
};

type InstrumentFunctionParams<Args extends unknown[], Result> = {
  /** The original function to wrap. */
  fn: (...args: Args) => Result;
  /** Human-readable name recorded in every emitted event. */
  functionName: string;
  /**
   * Returns the current `DynamicCore` instance, or `undefined` when the SDK
   * has not yet been initialised. Instrumentation is skipped when this returns
   * `undefined` or when `core.instrumentation.config.enabled` is `false`.
   */
  getCore: () => DynamicCore | undefined;
  /**
   * Optional explicit payload selectors. When provided, their returned
   * {@link SafePayload} is attached to the corresponding event. When omitted,
   * events carry only the safe envelope.
   */
  selectors?: InstrumentationPayloadSelectors<Args, Result>;
};

/**
 * Wraps a function so that each invocation emits a structured
 * `FunctionInstrumentationEvent` at three lifecycle points:
 *
 * - **started** — immediately before the function body executes.
 * - **resolved** — after the function (or its returned Promise) settles
 *   successfully, with the call duration.
 * - **rejected** — if the function throws synchronously or its returned
 *   Promise rejects, with the duration and the error's class name/code.
 *
 * Events carry only the safe envelope (function name, phase, timing, error
 * class, SDK/runtime metadata, identifiers). **Raw arguments and return values
 * are never captured.** A function that needs additional fields supplies
 * `selectors` that build an explicit, primitive-only {@link SafePayload} — the
 * only path from arguments/return value into a log.
 *
 * The original return value (or thrown error) is always forwarded to the
 * caller unchanged — instrumentation is purely observational, and a throwing
 * selector cannot change that.
 *
 * ## Providing selectors
 *
 * A function opts into a payload by supplying selectors that map its arguments
 * (`started`) and/or its awaited return value (`resolved`) to a primitive-only
 * {@link SafePayload}. Pick individual known-safe fields — never spread a whole
 * object, which is exactly what the model prevents.
 *
 * ```ts
 * const transfer = instrumentFunction({
 *   fn: rawTransfer,
 *   functionName: 'transfer',
 *   getCore: () => core,
 *   selectors: {
 *     // `args` is the argument tuple the wrapped function was called with.
 *     started: ([{ chainId, amount }]) => ({ amount, chainId }),
 *     // `result` is the awaited return value (never the Promise).
 *     resolved: ({ txHash }) => ({ txHash }),
 *   },
 * });
 * ```
 *
 * A selector that throws (or whose output fails scrubbing) is swallowed: the
 * event is still emitted without a payload, and the error is logged at `debug`.
 *
 * ## How functions are wrapped
 *
 * `instrumentFunction` is not called manually in application code. The esbuild
 * instrumentation plugin (`tools/instrumentation-plugin`) injects it at build
 * time for any exported function annotated with `@instrumented` in its JSDoc.
 *
 * ## Opting out
 *
 * Instrumentation is skipped entirely when `getCore()` returns `undefined`
 * (SDK not initialised) or when `core.instrumentation.config.enabled` is
 * `false`; in both cases the original function is called directly.
 * @notInstrumented
 */
// eslint-disable-next-line custom-rules/one-function-per-file
export const instrumentFunction = <Args extends unknown[], Result>({
  fn,
  functionName,
  getCore,
  selectors,
}: InstrumentFunctionParams<Args, Result>): ((...args: Args) => Result) => {
  const wrapped = (...args: Args): Result => {
    const core = getCore();

    if (!core?.instrumentation.config.enabled) {
      return fn(...args);
    }

    const { instrumentation, logger } = core;
    const { piiFields } = instrumentation.config;
    const base = { ...buildContext(core), functionName };
    const startedAt = Date.now();

    instrumentation.logFunction({
      ...base,
      payload: safeSelect({
        functionName,
        logger,
        piiFields,
        selector: selectors?.started,
        value: args,
      }),
      phase: 'started',
      status: 'info',
      timestamp: new Date().toISOString(),
    });

    const emitResolved = (value: Awaited<Result>): void => {
      instrumentation.logFunction({
        ...base,
        durationMs: Date.now() - startedAt,
        payload: safeSelect({
          functionName,
          logger,
          piiFields,
          selector: selectors?.resolved,
          value,
        }),
        phase: 'resolved',
        status: 'info',
        timestamp: new Date().toISOString(),
      });
    };

    const emitRejected = (error: unknown): void => {
      instrumentation.logFunction({
        ...base,
        ...toErrorFields(error),
        durationMs: Date.now() - startedAt,
        phase: 'rejected',
        status: 'error',
        timestamp: new Date().toISOString(),
      });
    };

    try {
      const result = fn(...args);

      if (result instanceof Promise) {
        return result.then(
          (value) => {
            emitResolved(value);
            return value;
          },
          (error: unknown) => {
            emitRejected(error);
            throw error;
          }
        ) as Result;
      }

      emitResolved(result as Awaited<Result>);
      return result;
    } catch (error) {
      emitRejected(error);
      throw error;
    }
  };

  return wrapped;
};
